/**
 * Launchpad Payment Service
 * 
 * Frontend service for handling launchpad payment-related API calls.
 */

import { apiConfig } from "@/config/api";
import { apiFetch } from "@/lib/apiFetch";

export interface LaunchpadPaymentStatus {
  needPayment: boolean;
  callsign: string;
  description: string;
  amount: number;
}

class LaunchpadPaymentService {
  /**
   * Check payment status for launchpad based on user's IAR type and status
   * @param callsign - User callsign
   * @returns Promise with payment status information
   */
  async checkStatusLaunchpad(callsign: string): Promise<LaunchpadPaymentStatus> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/payment/launchpad/check-status?callsign=${encodeURIComponent(callsign)}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data as LaunchpadPaymentStatus;
    } catch (error) {
      console.error("Error checking launchpad payment status:", error);
      throw error;
    }
  }
  /**
   * Create a launchpad payment and get Midtrans Snap redirect URL
   */
  async createPayment(callsign: string): Promise<{ token: string; redirect_url: string; orderId: string }> {
    const response = await apiFetch(
      `${apiConfig.baseURL}/api/payment/launchpad/create`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ callsign }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  }
}

export const launchpadPaymentService = new LaunchpadPaymentService();

